import React, { useState, useMemo } from 'react';
import TeacherStatusBadge from './TeacherStatusBadge';
import TeacherClassAccessModal from './TeacherClassAccessModal';
import TeacherPhotoModal from './TeacherPhotoModal';
import { API_BASE_URL } from '../../config.js';

const getFullAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const WING_BADGES = {
  PRE_PRIMARY: { label: '🧸 Pre-Primary', bg: '#fdf2f8', text: '#be185d', border: '#fbcfe8' },
  PRIMARY: { label: '🎒 Primary (1st–5th)', bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  MIDDLE_SCHOOL: { label: '📚 Middle (6th–8th)', bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  HIGH_SCHOOL: { label: '🎓 High School (9th–10th)', bg: '#faf5ff', text: '#7e22ce', border: '#e9d5ff' },
  PUC: { label: '🎓 PUC (11th–12th)', bg: '#fffbeb', text: '#b45309', border: '#fde68a' },
  ALL: { label: '🌟 All Classes', bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  ALL_CLASSES: { label: '🌟 All Classes', bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  ALLOCATED_ONLY: { label: '🎯 Allocated Only', bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' }
};

const TeacherDirectory = ({
  teachers = [],
  loading = false,
  orgName = 'School ERP',
  orgId = '',
  academicYear = '2026-2027',
  token = '',
  onAddTeacher,
  onEditTeacher,
  onDeleteTeacher,
  onToggleStatus,
  onExportData,
  onRefresh,
  onSelectTeacher
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [designationFilter, setDesignationFilter] = useState('ALL');
  const [wingFilter, setWingFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [accessModalTeacher, setAccessModalTeacher] = useState(null);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [photoModalTeacher, setPhotoModalTeacher] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const pageSize = 10;

  // Unrestricted teachers count (for School Admin warning banner)
  const unrestrictedTeachers = useMemo(() => {
    return teachers.filter(t => !t.accessMode || t.accessMode === 'ALL_CLASSES');
  }, [teachers]);

  // 1. Dynamic KPI Calculations
  const kpis = useMemo(() => {
    const total = teachers.length;
    const active = teachers.filter(t => (t.status || 'ACTIVE').toUpperCase() === 'ACTIVE').length;
    
    // Teaching staff roles (PGT, TGT, PRT, Teacher, Lecturer, Professor, Headmaster)
    const teachingStaff = teachers.filter(t => {
      const des = (t.designation || '').toUpperCase();
      return des.includes('PGT') || des.includes('TGT') || des.includes('PRT') || des.includes('TEACHER') || des.includes('LECTURER') || des.includes('PROFESSOR') || des.includes('HEADMASTER');
    }).length;

    const totalExp = teachers.reduce((acc, t) => acc + Number(t.experience || 0), 0);
    const avgExp = total > 0 ? (totalExp / total).toFixed(1) : '0';

    return { total, active, teachingStaff, avgExp };
  }, [teachers]);

  // Extract unique designation options
  const uniqueDesignations = useMemo(() => {
    const set = new Set();
    teachers.forEach(t => {
      if (t.designation) set.add(t.designation);
    });
    return Array.from(set);
  }, [teachers]);

  // 2. Search and Multi-Filter Logic
  const filteredTeachers = useMemo(() => {
    return teachers.filter(t => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || (
        (t.teacherName && t.teacherName.toLowerCase().includes(term)) ||
        (t.teacherId && t.teacherId.toLowerCase().includes(term)) ||
        (t.phoneNumber && t.phoneNumber.toLowerCase().includes(term)) ||
        (t.gmail && t.gmail.toLowerCase().includes(term)) ||
        (t.designation && t.designation.toLowerCase().includes(term)) ||
        (t.qualifications && t.qualifications.toLowerCase().includes(term)) ||
        (t.specialization && t.specialization.toLowerCase().includes(term))
      );

      const matchesDesignation = designationFilter === 'ALL' || t.designation === designationFilter;
      const matchesWing = wingFilter === 'ALL' || 
        (wingFilter === 'UNRESTRICTED' && (!t.accessMode || t.accessMode === 'ALL_CLASSES')) ||
        (t.teachingWing === wingFilter || t.accessMode === wingFilter);

      const statusUpper = (t.status || 'ACTIVE').toUpperCase();
      const matchesStatus = statusFilter === 'ALL' || statusUpper === statusFilter;

      return matchesSearch && matchesDesignation && matchesWing && matchesStatus;
    });
  }, [teachers, searchTerm, designationFilter, wingFilter, statusFilter]);

  // 3. Client-side Pagination
  const totalPages = Math.ceil(filteredTeachers.length / pageSize) || 1;
  const paginatedTeachers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTeachers.slice(start, start + pageSize);
  }, [filteredTeachers, currentPage]);

  const getInitials = (name) => {
    if (!name) return 'TC';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const getWingBadge = (teacher) => {
    if (!teacher.accessMode || teacher.accessMode === 'ALL_CLASSES') {
      return WING_BADGES.ALL_CLASSES;
    }
    if (teacher.accessMode === 'ALLOCATED_ONLY') {
      return WING_BADGES.ALLOCATED_ONLY;
    }
    return WING_BADGES[teacher.teachingWing] || WING_BADGES.PRIMARY;
  };

  const handleOpenAccessModal = (teacher, e) => {
    if (e) e.stopPropagation();
    setAccessModalTeacher(teacher);
    setIsAccessModalOpen(true);
  };

  const handleAccessSaved = (savedData) => {
    if (accessModalTeacher && savedData) {
      accessModalTeacher.accessMode = savedData.accessMode;
      accessModalTeacher.teachingWing = savedData.teachingWing;
    }
    if (onRefresh) onRefresh();
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '24px 32px', backgroundColor: '#f8fafc', overflowY: 'auto' }}>
      {/* Top Banner Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#0f172a' }}>
            Faculty & Teaching Staff Directory
          </h2>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
            {orgName} • Manage educators, teaching wings (Primary/High School), and class access permissions
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="fs-btn-secondary"
            onClick={onRefresh}
            disabled={loading}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            🔄 {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            className="fs-btn-secondary"
            onClick={onExportData}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            📥 Export Data
          </button>
          <button
            type="button"
            className="fs-btn-primary"
            onClick={onAddTeacher}
            style={{ padding: '8px 18px', fontSize: '13px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600' }}
          >
            ➕ Add Teacher
          </button>
        </div>
      </div>

      {/* ⚠️ ADMIN WARNING BANNER: Unrestricted Teachers */}
      {unrestrictedTeachers.length > 0 && (
        <div
          style={{
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: '1px solid #fde68a',
            borderRadius: '12px',
            padding: '14px 20px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#92400e' }}>
                {unrestrictedTeachers.length} {unrestrictedTeachers.length === 1 ? 'teacher has' : 'teachers have'} unrestricted school-wide class access
              </div>
              <div style={{ fontSize: '12px', color: '#b45309', marginTop: '2px' }}>
                Configure Primary vs High School wing scoping to prevent cross-grade student data access.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setWingFilter('UNRESTRICTED')}
            style={{
              padding: '6px 14px',
              fontSize: '12.5px',
              fontWeight: '700',
              color: '#92400e',
              background: '#ffffff',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            🔍 View Unrestricted ({unrestrictedTeachers.length})
          </button>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
        <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Total Faculty</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginTop: '4px' }}>{kpis.total}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#16a34a' }}>Active Faculty</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#166534', marginTop: '4px' }}>{kpis.active}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#2563eb' }}>Teaching Staff</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#1d4ed8', marginTop: '4px' }}>{kpis.teachingStaff}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#d97706' }}>Avg Experience</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#b45309', marginTop: '4px' }}>{kpis.avgExp} Yrs</div>
        </div>
      </div>

      {/* Toolbar: Search, Wing & Designation Filters */}
      <div style={{ background: '#ffffff', borderRadius: '12px 12px 0 0', padding: '14px 20px', border: '1px solid #e2e8f0', borderBottom: 'none', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search faculty by name, ID, phone, email, or qualification..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: '100%', paddingLeft: '14px', height: '38px', fontSize: '13px' }}
          />
        </div>

        {/* Wing Filter */}
        <select
          className="form-control"
          value={wingFilter}
          onChange={(e) => {
            setWingFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '180px', height: '38px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}
        >
          <option value="ALL">🏫 All Wings / Scopes</option>
          <option value="PRIMARY">🎒 Primary (1st–5th)</option>
          <option value="HIGH_SCHOOL">🎓 High School (9th–10th)</option>
          <option value="MIDDLE_SCHOOL">📚 Middle School (6th–8th)</option>
          <option value="PRE_PRIMARY">🧸 Pre-Primary</option>
          <option value="ALLOCATED_ONLY">🎯 Specific Allocated</option>
          <option value="UNRESTRICTED">⚠️ Unrestricted Access</option>
        </select>

        {/* Designation Filter */}
        <select
          className="form-control"
          value={designationFilter}
          onChange={(e) => {
            setDesignationFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '160px', height: '38px', fontSize: '13px' }}
        >
          <option value="ALL">All Designations</option>
          {uniqueDesignations.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          className="form-control"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '130px', height: '38px', fontSize: '13px' }}
        >
          <option value="ALL">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {/* 6-Column High-Readability Enterprise Table */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0 0 12px 12px', overflow: 'hidden', flex: 1 }}>
        <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          <table className="fs-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>FACULTY</th>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>DESIGNATION</th>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>TEACHING SCOPE & WING</th>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>CONTACT</th>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>STATUS</th>
                <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#475569' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading faculty directory...</td>
                </tr>
              ) : paginatedTeachers.length > 0 ? (
                paginatedTeachers.map((t) => {
                  const wingBadge = getWingBadge(t);
                  return (
                    <tr
                      key={t.teacherId}
                      onClick={() => onSelectTeacher(t)}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        transition: 'background 0.15s ease'
                      }}
                      className="hover:bg-slate-50"
                    >
                      {/* Column 1: FACULTY */}
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setPhotoModalTeacher(t);
                              setIsPhotoModalOpen(true);
                            }}
                            title="Click to view full photo & mobile update permissions"
                            style={{
                              position: 'relative',
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: '700',
                              flexShrink: 0,
                              cursor: 'pointer',
                              overflow: 'visible',
                              border: (t.photoUrl || t.photo) ? '2px solid #3b82f6' : '2px solid transparent',
                              transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.08)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {(t.photoUrl || t.photo) ? (
                                <img
                                  key={t.photoUrl || t.photo || t.teacherId}
                                  src={getFullAssetUrl(t.photoUrl || t.photo)}
                                  alt={t.teacherName}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'inline';
                                  }}
                                />
                              ) : null}
                              <span style={{ display: (t.photoUrl || t.photo) ? 'none' : 'inline' }}>
                                {getInitials(t.teacherName)}
                              </span>
                            </div>
                            {/* Permission indicator badge */}
                            <span
                              title={t.canUpdatePhoto ? 'Mobile Photo Update: Allowed' : 'Mobile Photo Update: Locked'}
                              style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                backgroundColor: t.canUpdatePhoto ? '#22c55e' : '#94a3b8',
                                border: '2px solid #ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px'
                              }}
                            />
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#0f172a' }}>
                              {t.teacherName}
                            </div>
                            <div style={{ fontSize: '11.5px', fontFamily: 'monospace', color: '#2563eb', fontWeight: '600' }}>
                              {t.teacherId}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Column 2: DESIGNATION */}
                      <td style={{ padding: '12px 20px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid #dbeafe',
                            marginBottom: '2px'
                          }}
                        >
                          {t.designation || 'Faculty'}
                        </span>
                        <div style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                          {t.specialization || 'General'}
                        </div>
                      </td>

                      {/* Column 3: TEACHING SCOPE & WING */}
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '3px 9px',
                              borderRadius: '6px',
                              background: wingBadge.bg,
                              color: wingBadge.text,
                              border: `1px solid ${wingBadge.border}`,
                              fontSize: '12px',
                              fontWeight: '700'
                            }}
                          >
                            {wingBadge.label}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          Mode: {t.accessMode || 'ALL_CLASSES'}
                        </div>
                      </td>

                      {/* Column 4: CONTACT */}
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ fontSize: '12.5px', color: '#1e293b', fontWeight: '500' }}>
                          📞 {t.phoneNumber || '—'}
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                          ✉ {t.gmail || '—'}
                        </div>
                      </td>

                      {/* Column 5: STATUS */}
                      <td style={{ padding: '12px 20px' }}>
                        <TeacherStatusBadge status={t.status} />
                      </td>

                      {/* Column 6: ACTIONS */}
                      <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="fs-btn-secondary"
                            onClick={(e) => handleOpenAccessModal(t, e)}
                            style={{ padding: '4px 10px', fontSize: '11.5px', background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe', fontWeight: '600' }}
                            title="Manage Wing & Class Access Permissions"
                          >
                            🛡️ Class Access
                          </button>
                          <button
                            type="button"
                            className="fs-btn-secondary"
                            onClick={() => onEditTeacher(t)}
                            style={{ padding: '4px 10px', fontSize: '11.5px' }}
                            title="Edit profile"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="fs-btn-secondary"
                            onClick={() => onToggleStatus(t)}
                            style={{ padding: '4px 8px', fontSize: '11.5px' }}
                            title={t.status === 'ACTIVE' ? 'Deactivate teacher' : 'Activate teacher'}
                          >
                            {t.status === 'ACTIVE' ? '⏸️' : '▶️'}
                          </button>
                          <button
                            type="button"
                            className="fs-btn-secondary"
                            onClick={() => onDeleteTeacher(t)}
                            style={{ padding: '4px 8px', fontSize: '11.5px', color: '#ef4444', borderColor: '#fca5a5' }}
                            title="Delete teacher"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No faculty members found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar Footer */}
        <div style={{ padding: '14px 20px', background: '#ffffff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12.5px', color: '#64748b', fontWeight: '500' }}>
            Showing {filteredTeachers.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, filteredTeachers.length)} of {filteredTeachers.length} faculty members
          </div>

          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              className="fs-btn-secondary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              style={{ padding: '4px 12px', fontSize: '12px' }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: '12.5px', color: '#0f172a', fontWeight: '600', padding: '4px 8px' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="fs-btn-secondary"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              style={{ padding: '4px 12px', fontSize: '12px' }}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>

      {/* Teacher Class Access Modal */}
      <TeacherClassAccessModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        teacher={accessModalTeacher}
        orgId={orgId || accessModalTeacher?.orgId || ''}
        academicYear={academicYear || '2026-2027'}
        token={token}
        onSaveSuccess={handleAccessSaved}
      />

      {/* Teacher Full Photo Preview & Mobile Permission Modal */}
      <TeacherPhotoModal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        teacher={photoModalTeacher}
        token={token}
        onPhotoUpdated={(teacherId, newUrl) => {
          if (photoModalTeacher) {
            photoModalTeacher.photoUrl = newUrl;
            photoModalTeacher.photo = newUrl;
          }
          if (onRefresh) onRefresh();
        }}
        onPermissionUpdated={(teacherId, newPermission) => {
          if (photoModalTeacher) {
            photoModalTeacher.canUpdatePhoto = newPermission;
          }
          if (onRefresh) onRefresh();
        }}
      />
    </div>
  );
};

export default TeacherDirectory;
